import React from 'react';
import { useParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import TopBar from '../components/TopBar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../components/ui/breadcrumb';

const ResourceDetailPage = () => {
  const { id } = useParams();
  const { resources } = useData();
  const resource = resources.find(r => r.id === id);

  if (!resource) {
    return <div>Resource not found</div>;
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'hsl(var(--bg))' }}>
      <TopBar />

      <div className="wrap section">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/knowledge">Knowledge</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{resource.title}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="mb-6">
              <span className="text-xs px-2 py-1 rounded" style={{
                backgroundColor: resource.type === 'TOOL' ? 'hsl(var(--accent-soft))' : 'hsl(var(--blue-soft))',
                color: resource.type === 'TOOL' ? 'hsl(var(--accent-ink))' : 'hsl(var(--blue))'
              }}>
                {resource.type === 'TOOL' ? 'Interactive tool' : resource.type === 'POLICY' ? 'Policy' : 'Resource'}
              </span>
              <h1 className="mt-4 mb-3">{resource.title}</h1>
              <p className="text-lg" style={{ color: 'hsl(var(--ink-muted))' }}>{resource.summary}</p>
            </div>

            <Card className="p-6">
              <div className="prose max-w-none">
                <p>{resource.body}</p>
                
                {resource.type === 'TOOL' && (
                  <div className="mt-6 p-4 rounded-lg" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
                    <p className="text-sm font-medium mb-2">Interactive Tool Preview</p>
                    <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>This would render the interactive calculator/tool interface.</p>
                    <Button className="mt-3">Launch Tool</Button>
                  </div>
                )}

                {resource.fileUrl && (
                  <div className="mt-6">
                    <Button>Download PDF</Button>
                  </div>
                )}
              </div>
            </Card>

            <Card className="p-6 mt-6">
              <h3 className="mb-4">Discussion</h3>
              <p className="text-sm" style={{ color: 'hsl(var(--ink-muted))' }}>No discussions yet. Be the first to ask a question or share insights.</p>
              <Button variant="outline" className="mt-4">Start a discussion</Button>
            </Card>
          </div>

          <aside className="space-y-6">
            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">Resource Details</h4>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>Source</dt>
                  <dd>{resource.sourceOrg}</dd>
                </div>
                <div>
                  <dt className="font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>Published</dt>
                  <dd>{new Date(resource.publishedAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</dd>
                </div>
                <div>
                  <dt className="font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>Technologies</dt>
                  <dd className="flex flex-wrap gap-1 mt-1">
                    {resource.technologies.map(tech => (
                      <span key={tech} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>{tech}</span>
                    ))}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium" style={{ color: 'hsl(var(--ink-muted))' }}>Regions</dt>
                  <dd className="flex flex-wrap gap-1 mt-1">
                    {resource.regions.map(region => (
                      <span key={region} className="px-2 py-0.5 rounded text-xs" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>{region}</span>
                    ))}
                  </dd>
                </div>
              </dl>
            </Card>

            <Card className="p-5">
              <h4 className="text-sm font-semibold mb-3">Related Resources</h4>
              <div className="space-y-2">
                {resources.filter(r => r.id !== id && r.technologies.some(t => resource.technologies.includes(t))).slice(0, 3).map(related => (
                  <a key={related.id} href={`/knowledge/${related.id}`} className="block p-3 rounded-lg hover:shadow-md" style={{ backgroundColor: 'hsl(var(--surface-2))' }}>
                    <div className="text-sm font-medium mb-1">{related.title}</div>
                    <div className="text-xs" style={{ color: 'hsl(var(--ink-muted))' }}>{related.type}</div>
                  </a>
                ))}
              </div>
            </Card>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ResourceDetailPage;
